import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { getDefaultStore, useAtom } from "jotai";
import { useMemo, useState, type JSX } from "react";

import { graphql } from "@repo/graphql-types/gql";
import { CpsToast } from "corpus";

import {
  type RegulatoryLocationOption,
  RegulatoryLocationSelect,
} from "@/components/regulatory-documents/regulatory-location-select";
import { Button } from "@/components/ui/button/button";
import { ProgressRoundedLayout } from "@/components/ui/layout/progress-rounded-layout";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import {
  CardSelectDescription,
  CardSelectRoot,
  CardSelectTitle,
} from "@/components/ui/select-input/card-select";
import { useGraphQL } from "@/hooks/use-graphql";
import { regulatoryDocumentAtom } from "@/lib/atoms/regulatory-document-atom";
import {
  isReasonStepComplete,
  type RegulatoryDocumentLocationDraft,
  requiresLocationChoice,
} from "@/lib/form-schemas/regulatory-document-schema";
import {
  COD_TIPO_DECLARACAO_VINCULO,
  isBindingDeclaration,
  montaEnderecoDaUnidade,
  unidadesDoEstado,
} from "@/lib/helpers/regulatory-documents";
import { trackEvent } from "@/lib/tracking";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const RegulatoryDeclarationLocationsQuery = graphql(/* GraphQL */ `
  query RegulatoryDeclarationLocationsQuery(
    $codUsuario: Int!
    $codTipoDocumentoRegulatorio: Int!
  ) {
    usuariosAgendas(where: { ativo: { _eq: true }, codUsuario: { _eq: $codUsuario } }) {
      tbUsuariosAgendasUnidades(where: { ativo: { _eq: true } }) {
        Unidade {
          codUnidade
          nomeLimpo
          fotoUrl
          siglaUF
          ativo
          logradouro
          complemento
          bairro
          Cidade {
            nome
          }
        }
      }
    }
    tiposDocumentosRegulatorios(
      where: { codTipoDocumentoRegulatorio: { eq: $codTipoDocumentoRegulatorio } }
    ) {
      unidades {
        codUnidade
        cadastroReceituarioCompleto
        motivoIndisponibilidade
      }
    }
  }
`);

const RegulatoryDeclarationLocationsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [draft, setDraft] = useAtom(regulatoryDocumentAtom);
  const { user } = Route.useRouteContext();
  const [indisponivelAvisada, setIndisponivelAvisada] = useState("");

  const { data, isPending } = useGraphQL(RegulatoryDeclarationLocationsQuery, {
    codUsuario: user.codUsuario,
    codTipoDocumentoRegulatorio: COD_TIPO_DECLARACAO_VINCULO,
  });

  const disponiveis = useMemo(
    () => unidadesDoEstado(data, draft.siglaUF),
    [data, draft.siglaUF],
  );

  const motivosPorUnidade = useMemo(
    () =>
      new Map(
        (data?.tiposDocumentosRegulatorios?.[0]?.unidades ?? [])
          .filter((unidade) => Boolean(unidade.motivoIndisponibilidade))
          .map((unidade) => [unidade.codUnidade, unidade.motivoIndisponibilidade ?? ""]),
      ),
    [data],
  );

  const locations: RegulatoryLocationOption[] = useMemo(
    () =>
      disponiveis.map(({ unidade, apta }) => ({
        codUnidade: unidade.codUnidade,
        nome: unidade.nomeLimpo ?? "",
        fotoUrl: unidade.fotoUrl ?? "",
        unavailable: !apta,
        motivoIndisponibilidade: motivosPorUnidade.get(unidade.codUnidade),
      })),
    [disponiveis, motivosPorUnidade],
  );

  const selectedIds = draft.selectedLocations.map((location) =>
    String(location.codUnidade),
  );

  const handleSelectionChange = (values: string[]): void => {
    const escolhidas: RegulatoryDocumentLocationDraft[] = values.flatMap((value) => {
      const encontrada = disponiveis.find(
        ({ unidade }) => String(unidade.codUnidade) === value,
      );
      if (!encontrada) return [];

      return [
        {
          codUnidade: encontrada.unidade.codUnidade,
          nome: encontrada.unidade.nomeLimpo ?? "",
          endereco: montaEnderecoDaUnidade(encontrada.unidade),
        },
      ];
    });

    setDraft((prev) => ({ ...prev, selectedLocations: escolhidas }));
  };

  const handleUnavailableClick = (location: RegulatoryLocationOption): void => {
    setIndisponivelAvisada(
      location.motivoIndisponibilidade ??
        `${location.nome} ainda não pode assinar esta declaração`,
    );
  };

  const handleContinue = (): void => {
    if (draft.selectedLocations.length === 0) return;

    trackEvent("Unidade da Declaração Selecionada", {
      codMotivoDocumento: draft.codMotivoDocumento,
      siglaUF: draft.siglaUF,
      quantidadeUnidades: draft.selectedLocations.length,
    });

    void navigate({ to: "/settings/regulatory-documents/review" });
  };

  const indisponiveisSemMotivo = locations.filter(
    (location) => location.unavailable && !location.motivoIndisponibilidade,
  ).length;

  return (
    <ProgressRoundedLayout progress={3 / 4} className="pb-0">
      <div className="flex h-full flex-col justify-between gap-4">
        <div className="flex flex-col gap-3">
          <CardSelectRoot
            name="regulatory-declaration-location"
            mode="multi"
            defaultValue={selectedIds}
            onChange={handleSelectionChange}
          >
            <CardSelectTitle>Em qual unidade você vai retirar?</CardSelectTitle>
            <CardSelectDescription>
              Esta declaração é assinada à mão na unidade, então sai uma para cada uma que
              você escolher.
              {indisponiveisSemMotivo > 0
                ? " As desabilitadas ainda não têm o cadastro necessário."
                : ""}
            </CardSelectDescription>

            {isPending ? (
              <FullPageSpinner />
            ) : (
              <RegulatoryLocationSelect
                locations={locations}
                onUnavailableClick={handleUnavailableClick}
              />
            )}
          </CardSelectRoot>
        </div>

        <div className="sticky bottom-0 flex w-full flex-col bg-white pb-6 pt-4">
          <Button
            fullWidth
            onClick={handleContinue}
            disabled={draft.selectedLocations.length === 0}
          >
            Continuar
          </Button>
        </div>
      </div>

      <CpsToast
        title={indisponivelAvisada}
        show={indisponivelAvisada !== ""}
        onClose={() => {
          setIndisponivelAvisada("");
        }}
        type="warning"
        duration={4}
      />
    </ProgressRoundedLayout>
  );
};

export const Route = createFileRoute(
  "/settings/regulatory-documents/declaration-locations",
)({
  component: RegulatoryDeclarationLocationsPage,
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-documentos-regulatorios-no-app"]) {
      redirect({ to: "/settings", throw: true });
    }

    const draft = getDefaultStore().get(regulatoryDocumentAtom);

    if (
      !isReasonStepComplete(draft) ||
      !isBindingDeclaration(draft.codTipoDocumentoRegulatorio)
    ) {
      redirect({ to: "/settings/regulatory-documents", throw: true });
    }

    if (!draft.siglaUF) {
      redirect({ to: "/settings/regulatory-documents/state", throw: true });
    }

    if (!requiresLocationChoice(draft)) {
      redirect({ to: "/settings/regulatory-documents/review", throw: true });
    }
  },
});
